import { useEffect, useRef } from 'react'

/**
 * O teclado do app: o que cada tecla faz, e as regras de quando ela NÃO faz
 * nada. O ouvinte mora em `Atalhos.tsx`; aqui fica o que dá para testar sem
 * DOM de verdade.
 *
 * ## As telas não escutam teclado — elas se MARCAM
 *
 * Cada lista diz qual é o alvo principal de cada linha com `data-nav-linha`;
 * a busca da tela, com `data-atalho-busca`; o botão de criar, com
 * `data-atalho-novo`; o "Voltar", com `data-voltar`. Um ouvinte só lê essas
 * marcas na hora da tecla. A alternativa — cada tela com o seu `keydown` —
 * daria doze ouvintes discordando sobre quando ignorar a tecla (digitando?
 * com diálogo aberto?), e é exatamente essa regra que precisa ser uma só.
 *
 * O que não é marca, porque não é um elemento — trocar o mês —, a tela
 * registra com `useAtalhoDaTela`.
 */

/** `g` e depois a letra: as telas do app. Estilo GitHub, escolha do dono. */
export const IR_PARA: Readonly<Record<string, { rota: string; nome: string }>> = {
  d: { rota: '/', nome: 'Dashboard' },
  t: { rota: '/transacoes', nome: 'Transações' },
  p: { rota: '/produtos', nome: 'Produtos' },
  l: { rota: '/lembretes', nome: 'Lembretes' },
  n: { rota: '/notas', nome: 'Notas' },
  c: { rota: '/classificar', nome: 'Classificar' },
  r: { rota: '/relatorios', nome: 'Relatórios' },
  a: { rota: '/notificacoes', nome: 'Avisos' },
  s: { rota: '/ajustes', nome: 'Ajustes' },
}

/**
 * Quanto tempo o `g` espera pela letra. Longo o bastante para quem procura a
 * tecla, curto o bastante para um `g` perdido não sequestrar o próximo atalho
 * minutos depois.
 */
export const ESPERA_DO_G_MS = 1500

/**
 * O foco está num lugar onde a tecla é TEXTO? Então ela não é atalho: "g" num
 * campo de busca é a letra g, e seta num `<select>` troca a opção.
 */
export function estaDigitando(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) return false
  if (alvo.isContentEditable) return true
  const tag = alvo.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag !== 'INPUT') return false
  // Caixa e botão de rádio não recebem texto: seta e letra ali são do app.
  const tipo = (alvo as HTMLInputElement).type
  return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(tipo)
}

/**
 * Cmd, Ctrl e Alt são do sistema e do navegador — Cmd+T, Ctrl+F. Shift NÃO
 * conta: `?` é Shift+/ na maioria dos teclados.
 */
export function temModificador(e: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey' | 'altKey'>): boolean {
  return e.metaKey || e.ctrlKey || e.altKey
}

/**
 * A próxima linha ao apertar ↑ ou ↓.
 *
 * **Sem linha em foco, a primeira seta entra pela tela, e não pelo topo da
 * lista.** Quem rolou até o meio de setembro e aperta ↓ quer a linha que está
 * vendo — voltar ao dia 30 seria jogá-lo para cima. ↑ entra pela última
 * visível, pelo mesmo motivo.
 *
 * **Nas pontas, para.** Dar a volta levaria do último item de uma lista de
 * cem ao primeiro sem aviso nenhum.
 */
export function proximaLinha<T>(
  linhas: readonly T[],
  indiceAtual: number,
  direcao: 1 | -1,
  visivel: (linha: T) => boolean,
): T | null {
  if (linhas.length === 0) return null
  if (indiceAtual === -1) {
    const ordem = direcao === 1 ? linhas : [...linhas].reverse()
    return ordem.find(visivel) ?? ordem[0] ?? null
  }
  const proximo = Math.min(Math.max(indiceAtual + direcao, 0), linhas.length - 1)
  return linhas[proximo] ?? null
}

type AcaoDaTela = { tecla: string; descricao: string; fazer: () => void }

/**
 * As teclas que uma tela registrou. Global de módulo, e não contexto: só uma
 * tela está montada por vez, e o ouvinte precisa lê-las fora da árvore do
 * React, no `keydown`.
 */
const daTela = new Map<string, AcaoDaTela>()

/** A ação que a tela montada deu a esta tecla, se deu. */
export function acaoDaTela(tecla: string): AcaoDaTela | null {
  return daTela.get(tecla) ?? null
}

/** Todas, para a folha de ajuda dizer o que vale AQUI. */
export function acoesDaTela(): AcaoDaTela[] {
  return [...daTela.values()]
}

/**
 * Dá a uma tecla uma ação enquanto a tela estiver montada.
 *
 * `fazer` muda a cada render de quem chama (é uma closure sobre o mês, por
 * exemplo), então ele mora num ref atualizado depois do render — e o registro
 * chama sempre o mais novo, sem desregistrar e registrar a cada render.
 */
export function useAtalhoDaTela(tecla: string, descricao: string, fazer: () => void): void {
  const atual = useRef(fazer)
  useEffect(() => {
    atual.current = fazer
  })
  useEffect(() => {
    const acao = {
      tecla,
      descricao,
      fazer: () => {
        atual.current()
      },
    }
    daTela.set(tecla, acao)
    return () => {
      if (daTela.get(tecla) === acao) daTela.delete(tecla)
    }
  }, [tecla, descricao])
}
